import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, Infinity, Play, Pause, Volume2, VolumeX, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';

// Import extracted components
import {
  VisualBreathing,
  WeatherWindow,
  GentleDrawing,
  BeachDay,
  VisualSorting,
  TeaCeremony,
  createAudioContext,
  ensureAudioResumed,
} from './breaktime';

// ============= MAIN BREAK TIME MODAL =============
const BreakTimeModal = ({ isOpen, onClose, initialActivity = null }) => {
  const [activity, setActivity] = useState(initialActivity);
  const [timer, setTimer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const audioCtxRef = useRef(null);
  
  // Set initial activity if provided
  useEffect(() => {
    if (initialActivity && !activity) {
      setActivity(initialActivity);
    }
  }, [initialActivity]);
  
  // Initialize AudioContext on mount (but not during render)
  useEffect(() => {
    if (isOpen && !audioCtxRef.current) {
      audioCtxRef.current = createAudioContext();
    }
  }, [isOpen]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioCtxRef.current) {
        try {
          audioCtxRef.current.close();
        } catch { /* ignore */ }
        audioCtxRef.current = null;
      }
    };
  }, []);
  
  useEffect(() => {
    if (timer && !isPaused && timeLeft > 0) {
      const interval = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) { onClose(); return 0; }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer, isPaused, timeLeft, onClose]);
  
  const startActivity = async (act, duration = null) => {
    // Resume AudioContext on user interaction (browsers require this)
    if (audioCtxRef.current) {
      await ensureAudioResumed(audioCtxRef.current);
    }
    setActivity(act);
    setTimer(duration);
    setTimeLeft(duration);
    setIsPaused(false);
  };
  
  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };
  
  if (!isOpen) return null;
  
  const activities = [
    { id: 'breathing', name: '🌬️ Visual Breathing', desc: 'Follow the rhythm' },
    { id: 'weather', name: '🌧️ Weather Window', desc: 'Watch the world' },
    { id: 'beach', name: '🏖️ Beach Day', desc: 'Waves & sunshine' },
    { id: 'drawing', name: '✨ Gentle Drawing', desc: 'Create flowing art' },
    { id: 'sorting', name: '🔲 Visual Sorting', desc: 'Organize shapes' },
    { id: 'tea', name: '🍵 Tea Ceremony', desc: 'Pour and arrange' },
  ];
  
  return (
    <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 text-white/80">
        <div className="flex items-center gap-4">
          {activity && (
            <button 
              onClick={() => { setActivity(null); setTimer(null); setTimeLeft(null); }}
              className="hover:text-white transition-colors"
            >
              ← Back
            </button>
          )}
          <h2 className="text-xl font-light">Take a Break</h2>
        </div>
        <div className="flex items-center gap-4">
          {/* Open in new tab button */}
          <button
            onClick={() => {
              const breakUrl = `${window.location.origin}/break${activity ? `?activity=${activity}` : ''}`;
              // Use authenticated tab opener if available, otherwise fallback
              if (window.openAuthenticatedTab) {
                window.openAuthenticatedTab(breakUrl);
              } else {
                window.open(breakUrl, '_blank', 'noopener,noreferrer');
              }
            }}
            className="p-2 rounded-full transition-all bg-white/5 text-white/60 hover:bg-white/10 hover:text-white"
            title="Open in new tab (for background music)"
          >
            <ExternalLink className="w-5 h-5" />
          </button>
          {/* Sound toggle */}
          <button
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2 rounded-full transition-all ${soundEnabled ? 'bg-white/20 text-white' : 'bg-white/5 text-white/40'}`}
            title={soundEnabled ? 'Sound On' : 'Sound Off'}
          >
            {soundEnabled ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
          </button>
          {/* Timer display */}
          {timeLeft !== null && (
            <div className="flex items-center gap-2 bg-white/10 rounded-full px-4 py-1">
              <button onClick={() => setIsPaused(!isPaused)} className="hover:text-white">
                {isPaused ? <Play className="w-4 h-4" /> : <Pause className="w-4 h-4" />}
              </button>
              <span className="text-sm font-mono">{formatTime(timeLeft)}</span>
            </div>
          )}
          {timer === null && activity && (
            <div className="flex items-center gap-1 text-white/50 text-sm">
              <Infinity className="w-4 h-4" />
              <span>No timer</span>
            </div>
          )}
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-auto">
        {!activity ? (
          // Activity selection
          <div className="flex flex-col items-center justify-center h-full px-4">
            <p className="text-white/60 text-lg mb-8">Choose your moment of calm</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl">
              {activities.map(act => (
                <button
                  key={act.id}
                  onClick={() => startActivity(act.id)}
                  className="bg-white/10 hover:bg-white/20 rounded-2xl p-6 text-left transition-all transform hover:scale-105"
                >
                  <p className="text-2xl mb-2">{act.name.split(' ')[0]}</p>
                  <p className="text-white font-medium">{act.name.split(' ').slice(1).join(' ')}</p>
                  <p className="text-white/50 text-sm mt-1">{act.desc}</p>
                </button>
              ))}
            </div>
            
            {/* Timer options */}
            <div className="mt-12">
              <p className="text-white/40 text-sm mb-4 text-center">Or set a timer:</p>
              <div className="flex gap-3">
                {[1, 3, 5, 10].map(min => (
                  <Button
                    key={min}
                    variant="outline"
                    onClick={() => startActivity('breathing', min * 60)}
                    className="bg-white/5 border-white/20 text-white/70 hover:bg-white/10 hover:text-white"
                  >
                    <Clock className="w-4 h-4 mr-2" />
                    {min} min
                  </Button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          // Activity view
          <div className="h-full">
            {activity === 'breathing' && <VisualBreathing audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
            {activity === 'weather' && <WeatherWindow audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
            {activity === 'beach' && <BeachDay audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
            {activity === 'drawing' && <GentleDrawing audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
            {activity === 'sorting' && <VisualSorting audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
            {activity === 'tea' && <TeaCeremony audioCtxRef={audioCtxRef} soundEnabled={soundEnabled} />}
          </div>
        )}
      </div>
      
      {/* Footer */}
      <div className="p-2 text-center text-white/30 text-xs">
        Audio generated using Web Audio API • Take care of yourself 💚
      </div>
    </div>
  );
};

// ============= SELF-CONTAINED WRAPPER WITH KONAMI CODE + FOOTER LINK =============
// Konami code constant (defined outside component to be stable)
const KONAMI_CODE = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'KeyB', 'KeyA'];

export const BreakTime = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const konamiRef = useRef([]);
  const lastScrollY = useRef(0);
  
  useEffect(() => {
    const handleKeyDown = (e) => {
      konamiRef.current.push(e.code);
      konamiRef.current = konamiRef.current.slice(-10);
      
      if (konamiRef.current.join(',') === KONAMI_CODE.join(',')) {
        setIsOpen(true);
        konamiRef.current = [];
      }
    };
    
    // Only show when near the bottom of the page
    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;
      const clientHeight = window.innerHeight;
      
      // Calculate how far from the bottom (in pixels)
      const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
      
      // Show only when within 200px of the bottom
      if (distanceFromBottom < 200) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
      
      lastScrollY.current = scrollTop;
    };
    
    // Initial check
    handleScroll();
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  
  return (
    <>
      {/* Fixed bottom button - only shows near bottom of page */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-2 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 text-gray-500/80 hover:text-gray-700 transition-all duration-500 px-4 py-2 hover:bg-white/90 rounded-full border border-gray-200/60 hover:border-gray-300 hover:shadow-md bg-white/70 backdrop-blur-sm ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'
        }`}
        title="Take a break (or try ↑↑↓↓←→←→BA)"
      >
        <span className="text-xl">☕</span>
        <span className="text-sm font-medium hidden sm:inline">Take a Break</span>
      </button>
      
      {/* Modal */}
      <BreakTimeModal isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default BreakTime;
export { BreakTimeModal };
